import React, { useState, useCallback } from "react";
import { ReactComponent as TokenASvg } from "../img/tokenA.svg";
import { ReactComponent as TokenBSvg } from "../img/tokenB.svg";
import { ReactComponent as RefreshSvg } from "../img/refresh-line.svg";
import { stark } from "starknet";
import { waitForTxV2, debounce } from "../utils";
import { CONTRACT_ADDRESS } from "../constants";
import "../styles/spin.css";
import { getStarknet } from "@argent/get-starknet";

const { getSelectorFromName } = stark;

function Swap({
  pendingTransactions,
  changePendingTransactions,
  account,
  poolABalance,
  poolBBalance,
}: {
  pendingTransactions: Object;
  changePendingTransactions: Function;
  account: string;
  poolABalance: number;
  poolBBalance: number;
}) {
  const [tokenAInput, changeTokenAInput] = useState(0);
  const [tokenBInput, changeTokenBInput] = useState(0);
  const [reverseToken, changeReverseToken] = useState(false);

  const debounceReceiveAmount = useCallback(
    (value) => {
      const receiveAmount = debounce((value: any) => {
        if (reverseToken) {
          const amount = (poolABalance * value) / (poolBBalance + value);
          changeTokenAInput(Number(amount.toFixed(4)));
        } else {
          const amount = (poolBBalance * value) / (poolABalance + value);
          changeTokenBInput(Number(amount.toFixed(4)));
        }
      });

      receiveAmount(value);
    },
    [reverseToken, poolABalance, poolBBalance]
  );

  const debouncePayAmount = useCallback(
    (value) => {
      const payAmount = debounce((value: any) => {
        if (reverseToken) {
          const amount = (poolBBalance * value) / (poolABalance - value);
          changeTokenBInput(Number(amount.toFixed(4)));
        } else {
          const amount = (poolABalance * value) / (poolBBalance - value);
          changeTokenAInput(Number(amount.toFixed(4)));
        }
      });

      return payAmount(value);
    },
    [reverseToken, poolABalance, poolBBalance]
  );

  async function doSwap() {
    const token = reverseToken ? "2" : "1";
    const amount = reverseToken ? `${tokenBInput}` : `${tokenAInput}`;
    let provider;

    const starknet = getStarknet();
    // try {
    //   await starknet.enable();

    //   if (starknet.isConnected) {
    //     // If the extension was installed and successfully connected, you have access to a starknet.js Signer object to do all kind of requests through the users wallet contract.
    //     provider = starknet.signer;
    //   } else {
    //     // In case the extension wasn't successfully connected you still have access to a starknet.js Provider to read starknet states and sent anonymous transactions
    //     provider = starknet.provider;
    //   }
    // } catch (error) {
    //   console.log(error);
    //   provider = starknet.provider;
    // }
    provider = starknet.provider;

    const swapTokenResponse = await provider.addTransaction({
      type: "INVOKE_FUNCTION",
      contract_address: CONTRACT_ADDRESS,
      entry_point_selector: getSelectorFromName("swap"),
      calldata: [account, token, amount],
    });
    const transactionHash = swapTokenResponse.transaction_hash;
    const transactionResponse = await provider.getTransactionStatus(
      transactionHash
    );
    changePendingTransactions({
      ...pendingTransactions,
      [transactionHash]: transactionResponse.tx_status,
    });

    waitForTxV2(transactionHash, (transactionStatus: any) => {
      changePendingTransactions({
        ...pendingTransactions,
        [transactionHash]: transactionStatus,
      });
    });
  }

  return (
    <div className="border-white border rounded-sm flex flex-col p-10 gap-y-6">
      <div className="flex justify-center">
        {reverseToken ? (
          <TokenBSvg className="w-11" />
        ) : (
          <TokenASvg className="w-11" />
        )}
        <input
          autoFocus
          className="text-3xl text-right rounded-sm ml-4 pr-3 mt-2 mb-2 font-medium border border-white focus:outline-none focus:bg-white focus:text-black bg-transparent text-white"
          type="text"
          size={10}
          value={reverseToken ? tokenBInput : tokenAInput}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const regex = /^[0-9\b]+$/;
            if (event.target.value === "" || regex.test(event.target.value)) {
              if (reverseToken) {
                changeTokenBInput(Number(event.target.value));
              } else {
                changeTokenAInput(Number(event.target.value));
              }
              debounceReceiveAmount(Number(event.target.value));
            }
          }}
        />
        <span className="text-white self-center ml-4 font-semibold text-3xl">
          {reverseToken ? "BTK" : "ATK"}
        </span>
      </div>
      <div className="flex justify-between items-center">
        <RefreshAnim
          reverseToken={() => {
            changeReverseToken(!reverseToken);
          }}
        />
        <ArrowAnim />
      </div>
      <div className="flex justify-center">
        {reverseToken ? (
          <TokenASvg className="w-11" />
        ) : (
          <TokenBSvg className="w-11" />
        )}
        <input
          className="text-3xl text-right rounded-sm ml-4 pr-3 mt-2 mb-2 font-medium focus:outline-none border border-white focus:bg-white focus:text-black bg-transparent text-white"
          type="text"
          size={10}
          value={reverseToken ? tokenAInput : tokenBInput}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const regex = /^[0-9\b]+$/;
            if (event.target.value === "" || regex.test(event.target.value)) {
              if (reverseToken) {
                changeTokenAInput(Number(event.target.value));
              } else {
                changeTokenBInput(Number(event.target.value));
              }
              debouncePayAmount(Number(event.target.value));
            }
          }}
        />
        <span className="text-white self-center ml-4 font-semibold text-3xl">
          {reverseToken ? "ATK" : "BTK"}
        </span>
      </div>
      <SwapButton doSwap={doSwap} />
    </div>
  );
}

function SwapButton({ doSwap }: { doSwap: Function }) {
  return (
    <div
      className="bg-transparent text-white border border-white rounded-sm text-2xl font-semibold w-full flex justify-center p-3 mt-4 cursor-pointer hover:bg-white hover:text-black"
      onClick={() => {
        doSwap();
      }}
    >
      <div>"SWAP"</div>
    </div>
  );
}

function ArrowAnim() {
  return (
    <div className="arrowAnim">
      <div className="arrowSliding">
        <div className="arrow"></div>
      </div>
      <div className="arrowSliding delay1">
        <div className="arrow"></div>
      </div>
    </div>
  );
}

function RefreshAnim({ reverseToken }: { reverseToken: Function }) {
  const [spin, spinAnim] = useState(true);

  return (
    <RefreshSvg
      className={`ml-2.5 cursor-pointer rotate ${spin ? "spin" : ""}`}
      onClick={() => {
        spinAnim(!spin);
        reverseToken();
      }}
    />
  );
}

export default Swap;
